import React, { useEffect, useState, useRef } from 'react';
import { StlViewer } from 'react-stl-viewer';
import { useViewportGrid } from '@ohif/ui-next';
import { useAppConfig } from '@state';
import { servicesManager } from '../../../app/src/App';
import { HexColorPicker } from 'react-colorful';
import * as THREE from 'three';
import {
  Eye,
  Grid3x3,
  CuboidIcon,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  Download,
  Printer,
  Lightbulb,
} from 'lucide-react';
import classNames from 'classnames';
import { STLAPIXRAD, STLAPI } from '../../../../constants/baseurl';
import { encryptAWSCredentials, validateEncryptionConfig } from '../utils/encryption';
import { getSpecificBucket } from '../../../../utils/bucket';

// Define interfaces for our component props and other types
interface STLViewportProps {
  viewportId: string;
  displaySets: any[];
  viewportOptions: any;
  servicesManager: any;
}

interface CameraRef {
  setCameraPosition: (position: CameraPosition) => void;
  getCurrentPosition: () => CameraPosition;
}

interface CameraPosition {
  latitude?: number;
  longitude?: number;
  distance?: number;
}

interface ModelDimensions {
  boundingRadius: number;
  width: number;
  height: number;
  length: number;
}

// Add smoothing parameters interface
interface SmoothingParams {
  alpha: number; // relaxation factor
  beta: number; // number of iterations
}

// Available 3D printing services
const PRINTING_SERVICES = [
  { name: 'Shapeways', url: 'https://www.shapeways.com/upload' },
  { name: 'Sculpteo', url: 'https://www.sculpteo.com/en/upload' },
  { name: 'i.materialise', url: 'https://i.materialise.com/en/3dprint' },
];

// Mesh types available
const MESH_TYPES = {
  DEFAULT: 'default',
  BONE: 'bone',
  SOFT_TISSUE: 'soft-tissue',
};

// Add this at the top of your file with other styles or in your global CSS
const styles = `
  @keyframes loadingBar {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

// Add this style tag right after your imports
const styleSheet = document.createElement('style');
styleSheet.textContent = styles;
document.head.appendChild(styleSheet);

// Add this interface to define the bucket data structure
interface BucketData {
  bucketName: string;
  accessKeyId: string;
  secretAccessKey: string;
  region?: string;
  dcmcAccessKey?: string;
  _id?: string;
  user?: string;
  bucketType?: string;
  __v?: number;
}

export default function STLViewport({
  viewportId,
  displaySets,
  viewportOptions,
}: STLViewportProps) {
  const [stlUrl, setStlUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [viewportGrid] = useViewportGrid();
  const [appConfig] = useAppConfig();
  const [dimensions, setDimensions] = useState({ width: 500, height: 600 });
  const [modelColor, setModelColor] = useState('rgb(50, 150, 250)');

  // Current mesh type
  const [currentMeshType, setCurrentMeshType] = useState(MESH_TYPES.DEFAULT);

  // Hologram mode state
  const [hologramMode, setHologramMode] = useState(false);
  const [hologramColor, setHologramColor] = useState('rgb(0, 195, 255)'); // Cyan blue for hologram
  const [showColorPicker, setShowColorPicker] = useState(false);

  // Print dialog
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [selectedPrintService, setSelectedPrintService] = useState(PRINTING_SERVICES[0]);
  const [printOptions, setPrintOptions] = useState({
    material: 'plastic',
    quality: 'standard',
    color: 'white',
  });

  // Add refs for camera and model
  const cameraRef = useRef<CameraRef | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const modelInfo = useRef<ModelDimensions | null>(null);

  const { uiNotificationService } = servicesManager.services;

  // Update dimensions on resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.clientWidth,
          height: Math.max(containerRef.current.clientHeight, 600),
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [viewportId]);

  // Initial camera setup with default view
  useEffect(() => {
    if (cameraRef.current) {
      // Set initial camera position
      cameraRef.current.setCameraPosition({
        latitude: Math.PI / 6, // 30 degrees
        longitude: Math.PI / 4, // 45 degrees
        distance: 3.5,
      });
    }
  }, [cameraRef.current]);

  // Hologram shader implementation
  const hologramVertexShader = `
varying vec2 vUv;
varying vec3 vPosition;
varying vec3 vNormal;

void main() {
  vUv = uv;
  vPosition = position;
  vNormal = normalize(normalMatrix * normal);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

  const hologramFragmentShader = `
uniform vec3 color;
uniform float time;
uniform float opacity;

varying vec2 vUv;
varying vec3 vPosition;
varying vec3 vNormal;

void main() {
  // Create scan line effect
  float scanLine = sin(vPosition.y * 20.0 + time) * 0.1 + 0.9;

  // Edge highlighting
  float fresnel = pow(1.0 - abs(dot(vNormal, vec3(0.0, 0.0, 1.0))), 2.0);

  // Combine effects
  vec3 glow = color * (fresnel * 0.5 + 0.5);
  vec3 finalColor = glow * scanLine;

  // Add some transparency based on fresnel effect
  float alpha = opacity * (0.5 + fresnel * 0.5);

  gl_FragColor = vec4(finalColor, alpha);
}
`;

  // Function to create a hologram material
  const createHologramMaterial = () => {
    const uniforms = {
      color: { value: new THREE.Color(hologramColor) },
      time: { value: 0 },
      opacity: { value: 1.0 },
    };

    return new THREE.ShaderMaterial({
      uniforms: uniforms,
      vertexShader: hologramVertexShader,
      fragmentShader: hologramFragmentShader,
      transparent: true,
      side: THREE.DoubleSide,
    });
  };

  // Animation loop for hologram effect
  useEffect(() => {
    if (!hologramMode) {
      return;
    }

    let animationFrameId: number;
    const clock = new THREE.Clock();

    const animate = () => {
      // Update time uniform for hologram shader
      const canvasId = `stl-canvas-${viewportId}`;
      const canvas = document.getElementById(canvasId);

      if (canvas) {
        const sceneProps = Object.keys(canvas).find(
          key => key.startsWith('__reactProps$') && canvas[key]?._owner?.stateNode?.scene
        );

        if (sceneProps) {
          const component = canvas[sceneProps]._owner.stateNode;
          if (component.scene) {
            // Find the mesh with our shader material
            component.scene.traverse(object => {
              if (object.isMesh && object.material.type === 'ShaderMaterial') {
                object.material.uniforms.time.value = clock.getElapsedTime();
              }
            });
          }
        }
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [hologramMode]);

  // Update the modelProps to include hologram mode
  const getModelProps = () => {
    if (hologramMode) {
      return {
        color: hologramColor,
        positionX: 0,
        positionY: 0,
        rotationX: 0,
        rotationY: 0,
        customMaterial: createHologramMaterial(),
      };
    }

    return {
      color: modelColor,
      positionX: 0,
      positionY: 0,
      rotationX: 0,
      rotationY: 0,
    };
  };

  // Handle model loading completion
  const handleFinishLoading = (dimensions: ModelDimensions) => {
    modelInfo.current = dimensions;
    console.log('Model loaded with dimensions:', dimensions);
    setLoading(false);

    // Set initial camera position based on model size
    if (cameraRef.current) {
      cameraRef.current.setCameraPosition({
        latitude: Math.PI / 6, // 30 degrees
        longitude: Math.PI / 4, // 45 degrees
        distance: 2.5, // Adjust based on model size
      });
    }
  };

  // Handle errors during loading
  const handleError = (err: Error) => {
    console.error('Error loading STL:', err);
    setError(err.message);
    setLoading(false);
    uiNotificationService.show({
      title: 'STL Loading Failed',
      message: err.message,
      type: 'error',
      duration: 5000,
    });
  };

  // Add smoothing state
  const [smoothingParams, setSmoothingParams] = useState<SmoothingParams>({
    alpha: 0.05, // default relaxation factor
    beta: 100, // default iterations
  });

  // Function to load STL model based on mesh type
  const loadMeshModel = async (meshType = MESH_TYPES.DEFAULT) => {
    if (!displaySets?.length) {
      return;
    }
    setCurrentMeshType(meshType);

    setLoading(true);
    setError(null);
    setDownloadProgress(0);

    const displaySet = displaySets[0];
    const formData = new FormData();
    const urlParams = new URLSearchParams(window.location.search);
    const hasPatientName = urlParams.has('PatientName');

    // Add required parameters from displaySet
    formData.append('SeriesInstanceUid', displaySet.SeriesInstanceUID);
    formData.append('StudyInstanceUid', displaySet.StudyInstanceUID);

    if (hasPatientName) {
      formData.append('patientPath', urlParams.get('PatientName'));
    } else {
      try {
        // Get bucket ID from URL or localStorage
        const bucketId = urlParams.get('bucketId') || localStorage.getItem('bucketId');

        if (bucketId) {
          // Dynamically fetch bucket credentials using the utility from bucket.js
          const bucketDataResult = await getSpecificBucket(bucketId);

          // Handle the different possible return types from getSpecificBucket
          let bucketData: BucketData | null = null;

          if (Array.isArray(bucketDataResult)) {
            // If it's an array (empty or not), we need to handle it
            if (bucketDataResult.length > 0) {
              // Take the first item if available
              bucketData = bucketDataResult[0] as BucketData;
            }
          } else if (bucketDataResult && typeof bucketDataResult === 'object') {
            // If it's an object with the expected properties
            bucketData = bucketDataResult as BucketData;
          }

          if (bucketData && bucketData.bucketName) {
            if (appConfig?.encryption && validateEncryptionConfig(appConfig.encryption)) {
              // Create AWS credentials object from bucket data
              const awsCredentials = {
                accessKeyId: bucketData.accessKeyId,
                secretAccessKey: bucketData.secretAccessKey,
                bucketName: bucketData.bucketName,
                region: bucketData.region || 'us-east-1',
                sessionToken: null,
              };

              try {
                // Encrypt the dynamically fetched credentials
                const encryptedCredentials = encryptAWSCredentials(
                  awsCredentials,
                  appConfig.encryption.encryptionKey
                );

                // Send encrypted credentials
                formData.append(
                  'encryptedCredentials',
                  JSON.stringify(encryptedCredentials.encryptedCredentials)
                );
                formData.append('region', encryptedCredentials.region || 'us-east-1');
                formData.append('bucket_name', encryptedCredentials.bucketName);
                formData.append('encryptionEnabled', 'true');
              } catch (error) {
                console.error('Failed to encrypt AWS credentials:', error);
                // Fallback to unencrypted (not recommended for production)
                formData.append('region', bucketData.region || 'us-east-1');
                formData.append('aws_access_key', bucketData.accessKeyId);
                formData.append('aws_secret_key', bucketData.secretAccessKey);
                formData.append('bucket_name', bucketData.bucketName);
                formData.append('encryptionEnabled', 'false');
              }
            } else {
              // Encryption not configured, send unencrypted
              formData.append('region', bucketData.region || 'us-east-1');
              formData.append('aws_access_key', bucketData.accessKeyId);
              formData.append('aws_secret_key', bucketData.secretAccessKey);
              formData.append('bucket_name', bucketData.bucketName);
              formData.append('encryptionEnabled', 'false');
            }
          } else {
            // Fallback to config if bucket data fetch fails
            fallbackToConfigCredentials(formData);
          }
        } else {
          // No bucket ID found, fallback to config
          fallbackToConfigCredentials(formData);
        }
      } catch (error) {
        console.error('Error fetching bucket data:', error);
        // Fallback to config credentials
        fallbackToConfigCredentials(formData);
      }
    }

    // Helper function for fallback to config credentials
    function fallbackToConfigCredentials(formData) {
      if (
        appConfig?.aws &&
        appConfig?.encryption &&
        validateEncryptionConfig(appConfig.encryption)
      ) {
        try {
          const encryptedCredentials = encryptAWSCredentials(
            appConfig.aws,
            appConfig.encryption.encryptionKey
          );

          formData.append(
            'encryptedCredentials',
            JSON.stringify(encryptedCredentials.encryptedCredentials)
          );
          formData.append('region', encryptedCredentials.region);
          formData.append('bucket_name', encryptedCredentials.bucketName);
          formData.append('encryptionEnabled', 'true');
        } catch (error) {
          console.error('Failed to encrypt config AWS credentials:', error);
          formData.append('region', appConfig?.aws?.region || '');
          formData.append('aws_access_key', appConfig?.aws?.accessKeyId || '');
          formData.append('aws_secret_key', appConfig?.aws?.secretAccessKey || '');
          formData.append('bucket_name', appConfig?.aws?.bucketName || '');
          formData.append('encryptionEnabled', 'false');
        }
      } else {
        formData.append('region', appConfig?.aws?.region || '');
        formData.append('aws_access_key', appConfig?.aws?.accessKeyId || '');
        formData.append('aws_secret_key', appConfig?.aws?.secretAccessKey || '');
        formData.append('bucket_name', appConfig?.aws?.bucketName || '');
        formData.append('encryptionEnabled', 'false');
      }
    }

    // Add smoothing parameters
    /* formData.append('alpha', smoothingParams.alpha.toString());
    formData.append('beta', smoothingParams.beta.toString()); */

    const isXRAD =
      window.location.href.includes('/xrad/') || window.location.href.includes('/radx/');
    const stlApi = isXRAD ? STLAPIXRAD : STLAPI;
    // Determine which endpoint to use based on mesh type
    let endpoint = `${stlApi}/generate-stl`;
    if (meshType === MESH_TYPES.BONE) {
      endpoint = `${stlApi}/generate-bone`;
    } else if (meshType === MESH_TYPES.SOFT_TISSUE) {
      endpoint = `${stlApi}/generate-soft-tissue`;
    }

    try {
      // Use fetch with progress tracking
      const response = await fetch(endpoint, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to generate ${meshType} mesh`);
      }

      // The API returns an STL file directly
      const blob = await response.blob();
      if (blob.type !== 'model/stl' && blob.type !== 'application/octet-stream') {
        throw new Error('Invalid response format from server');
      }

      // Revoke previous URL if it exists to prevent memory leaks
      if (stlUrl) {
        URL.revokeObjectURL(stlUrl);
      }

      // Create new blob URL
      const url = URL.createObjectURL(blob);
      setStlUrl(url);
      setLoading(false);
      setDownloadProgress(100);

      // Show success notification
      uiNotificationService.show({
        title: 'Model Generated',
        message: `${meshType} mesh has been successfully generated`,
        type: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error(`Error generating ${meshType} mesh:`, error);
      setError(error.message);
      setLoading(false);

      uiNotificationService.show({
        title: `${meshType.charAt(0).toUpperCase() + meshType.slice(1)} Mesh Generation Failed`,
        message: error.message,
        type: 'error',
        duration: 5000,
      });
    }
  };

  // Initial load
  useEffect(() => {
    loadMeshModel();

    // Cleanup function to revoke object URL when component unmounts
    return () => {
      if (stlUrl) {
        URL.revokeObjectURL(stlUrl);
      }
    };
  }, [displaySets, appConfig]);

  // Camera control functions
  const resetCamera = () => {
    if (cameraRef.current) {
      cameraRef.current.setCameraPosition({
        latitude: Math.PI / 6,
        longitude: Math.PI / 4,
        distance: 2.5,
      });
    }
  };

  // Various view orientations
  const topView = () => {
    if (cameraRef.current) {
      cameraRef.current.setCameraPosition({
        latitude: Math.PI / 2, // 90 degrees - directly above
        longitude: 0,
        distance: 2.5,
      });
    }
  };

  const bottomView = () => {
    if (cameraRef.current) {
      cameraRef.current.setCameraPosition({
        latitude: -Math.PI / 2, // -90 degrees - directly below
        longitude: 0,
        distance: 2.5,
      });
    }
  };

  const frontView = () => {
    if (cameraRef.current) {
      cameraRef.current.setCameraPosition({
        latitude: 0,
        longitude: 0,
        distance: 2.5,
      });
    }
  };

  const backView = () => {
    if (cameraRef.current) {
      cameraRef.current.setCameraPosition({
        latitude: 0,
        longitude: Math.PI, // 180 degrees - back
        distance: 2.5,
      });
    }
  };

  const leftView = () => {
    if (cameraRef.current) {
      cameraRef.current.setCameraPosition({
        latitude: 0,
        longitude: -Math.PI / 2, // -90 degrees
        distance: 2.5,
      });
    }
  };

  const rightView = () => {
    if (cameraRef.current) {
      cameraRef.current.setCameraPosition({
        latitude: 0,
        longitude: Math.PI / 2, // 90 degrees
        distance: 2.5,
      });
    }
  };

  const isometricView = () => {
    if (cameraRef.current) {
      cameraRef.current.setCameraPosition({
        latitude: Math.PI / 6, // 30 degrees
        longitude: Math.PI / 4, // 45 degrees
        distance: 2.5,
      });
    }
  };

  // Handle sending to 3D printing service
  const sendToPrintService = async () => {
    if (!stlUrl) {
      return;
    }

    try {
      // Fetch the STL file
      const response = await fetch(stlUrl);
      const blob = await response.blob();

      // Create a FormData object for the API request
      const formData = new FormData();
      formData.append('file', blob, 'model.stl');
      formData.append('material', printOptions.material);
      formData.append('quality', printOptions.quality);
      formData.append('color', printOptions.color);

      uiNotificationService.show({
        title: 'Preparing for 3D Printing',
        message: `Sending to ${selectedPrintService.name}...`,
        type: 'info',
        duration: 3000,
      });

      // Instead of making a real API call (which would require an actual API key and account),
      // we'll open the service's upload page in a new tab
      window.open(selectedPrintService.url, '_blank');

      setShowPrintDialog(false);
    } catch (error) {
      console.error('Error sending to print service:', error);
      uiNotificationService.show({
        title: 'Print Service Error',
        message: error.message,
        type: 'error',
        duration: 5000,
      });
    }
  };

  if (loading) {
    let loadingText;
    switch (currentMeshType) {
      case MESH_TYPES.BONE:
        loadingText = 'Loading Bone Mesh';
        break;
      case MESH_TYPES.SOFT_TISSUE:
        loadingText = 'Loading Soft Tissue Mesh';
        break;
      default:
        loadingText = 'Loading Default Mesh';
    }

    return (
      <div className="flex h-full w-full items-center justify-center bg-gray-900/50 backdrop-blur-sm">
        <div className="w-80 space-y-6 rounded-xl bg-gray-800/90 p-8 shadow-2xl">
          <div className="text-center">
            <h2 className="bg-gradient-to-r from-blue-400 to-teal-400 bg-clip-text text-2xl font-bold text-transparent">
              {loadingText}
              <span className="inline-block animate-bounce text-blue-400">.</span>
              <span className="inline-block animate-bounce text-teal-400 delay-150">.</span>
              <span className="inline-block animate-bounce text-cyan-400 delay-300">.</span>
            </h2>
          </div>

          <div className="relative h-2 overflow-hidden rounded-full bg-gray-700">
            <div
              className="h-full rounded-full bg-gradient-to-r from-blue-500 via-teal-500 to-blue-500 transition-all duration-300"
              style={{ width: `${downloadProgress}%` }}
            ></div>
          </div>

          <p className="text-center text-sm text-gray-400">Downloaded: {downloadProgress}%</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-red-500">
          Error: {error}
          <button
            onClick={() => loadMeshModel()}
            className="ml-2 rounded bg-blue-500 px-3 py-1 text-white hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!stlUrl) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-primary-light">No STL model available</div>
      </div>
    );
  }
  const style = {
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  };

  const layout = viewportGrid;

  return (
    <div
      ref={containerRef}
      id={`stl-container-${viewportId}`}
      style={{ width: '100%', height: '100%', position: 'relative' }}
      className="relative"
    >
      <StlViewer
        url={stlUrl}
        style={style}
        modelProps={getModelProps()}
        cameraProps={{
          ref: cameraRef,
          position: [0, 0, 20],
          fov: 45,
          zoom: 1,
        }}
        shadows={false}
        orbitControls={true}
        onFinishLoading={handleFinishLoading}
        onError={handleError}
        canvasId={`stl-canvas-${viewportId}`}
      />

      {/* Top toolbar with main controls */}
      <div
        className={
          'absolute left-4 top-4 z-10 inline-flex flex-wrap items-center justify-start gap-2 rounded-lg bg-gray-800 bg-opacity-70 p-2'
        }
      >
        {/* Mesh Type Selection */}
        <div className="relative min-w-[120px] md:min-w-[160px]">
          <select
            value={currentMeshType}
            onChange={e => loadMeshModel(e.target.value)}
            className="w-full rounded-md border border-gray-600 bg-gray-700 px-2 py-2 text-sm text-white hover:bg-gray-600 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 md:px-4"
          >
            <option value={MESH_TYPES.DEFAULT}>Default</option>
            <option value={MESH_TYPES.BONE}>Bone</option>
            <option value={MESH_TYPES.SOFT_TISSUE}>Soft</option>
          </select>
        </div>

        {/* Color Selection */}
        <div className="relative">
          <button
            onClick={() => setShowColorPicker(!showColorPicker)}
            className="flex items-center gap-2 rounded-md border border-gray-600 bg-gray-700 px-3 py-2 text-sm text-white hover:bg-gray-600 md:px-4"
          >
            <div
              className="h-4 w-4 rounded-full border border-white"
              style={{ backgroundColor: modelColor }}
            />
            <span className="hidden md:inline">Color</span>
          </button>
          {showColorPicker && (
            <div className="absolute left-0 mt-2 rounded-lg bg-gray-800 p-4 shadow-xl">
              <HexColorPicker
                color={modelColor}
                onChange={setModelColor}
              />
              <div className="mt-3 grid grid-cols-6 gap-2">
                {['#3296fa', '#32c832', '#c83232', '#c8c8c8', '#ffa500', '#ffffff'].map(color => (
                  <button
                    key={color}
                    onClick={() => {
                      setModelColor(color);
                      setShowColorPicker(false);
                    }}
                    className="h-6 w-6 rounded-full border border-gray-600 transition-transform hover:scale-110"
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="hidden h-8 w-px bg-gray-600 md:block"></div>

        {/* Other Controls */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setHologramMode(!hologramMode)}
            className={classNames(
              'group relative flex h-10 w-10 items-center justify-center rounded-md border transition-all duration-300 hover:w-32',
              hologramMode
                ? 'border-blue-500 bg-blue-600 hover:bg-blue-700'
                : 'border-gray-600 bg-gray-700 hover:bg-gray-600'
            )}
          >
            <Lightbulb
              size={16}
              className="absolute left-3 text-white"
            />
            <span className="invisible ml-6 text-white opacity-0 transition-opacity duration-300 group-hover:visible group-hover:opacity-100">
              Hologram
            </span>
          </button>
          <a
            href={stlUrl}
            download={`${displaySets[0]?.SeriesInstanceUID}_${currentMeshType === MESH_TYPES.SOFT_TISSUE ? 'soft' : currentMeshType === MESH_TYPES.BONE ? 'bone' : ''}.stl`}
            className="group relative flex h-10 w-10 items-center justify-center rounded-md border border-gray-600 bg-gray-700 transition-all duration-300 hover:w-32 hover:bg-gray-600"
          >
            <Download
              size={16}
              className="absolute left-3 text-white"
            />
            <span className="invisible ml-6 text-white opacity-0 transition-opacity duration-300 group-hover:visible group-hover:opacity-100">
              Download
            </span>
          </a>
          <button
            onClick={() => setShowPrintDialog(true)}
            className="group relative flex h-10 w-10 items-center justify-center rounded-md border border-gray-600 bg-gray-700 transition-all duration-300 hover:w-32 hover:bg-gray-600"
          >
            <Printer
              size={16}
              className="absolute left-3 text-white"
            />
            <span className="invisible ml-6 text-white opacity-0 transition-opacity duration-300 group-hover:visible group-hover:opacity-100">
              3D Print
            </span>
          </button>
        </div>
      </div>

      {/* Right sidebar with orientation controls */}
      <div className="absolute right-4 top-6 z-10 flex flex-col gap-2 rounded-lg bg-gray-800 bg-opacity-70 p-2">
        <button
          onClick={topView}
          className="rounded-md bg-gray-700 p-2 text-white transition-colors hover:bg-blue-600"
          title="Top View"
        >
          <ArrowUp size={20} />
        </button>
        <button
          onClick={frontView}
          className="rounded-md bg-gray-700 p-2 text-white transition-colors hover:bg-blue-600"
          title="Front View"
        >
          <CuboidIcon size={20} />
        </button>
        <button
          onClick={rightView}
          className="rounded-md bg-gray-700 p-2 text-white transition-colors hover:bg-blue-600"
          title="Right View"
        >
          <ArrowRight size={20} />
        </button>
        <button
          onClick={leftView}
          className="rounded-md bg-gray-700 p-2 text-white transition-colors hover:bg-blue-600"
          title="Left View"
        >
          <ArrowLeft size={20} />
        </button>
        <button
          onClick={backView}
          className="rounded-md bg-gray-700 p-2 text-white transition-colors hover:bg-blue-600"
          title="Back View"
        >
          <Grid3x3 size={20} />
        </button>
        <button
          onClick={bottomView}
          className="rounded-md bg-gray-700 p-2 text-white transition-colors hover:bg-blue-600"
          title="Bottom View"
        >
          <ArrowDown size={20} />
        </button>
        <button
          onClick={isometricView}
          className="rounded-md bg-gray-700 p-2 text-white transition-colors hover:bg-blue-600"
          title="Isometric View"
        >
          <Eye size={20} />
        </button>
      </div>

      {/* Smoothing Controls Panel - Positioned at bottom right */}
      {/*<div className="absolute bottom-6 right-4 z-10 w-72 rounded-lg bg-gray-800 bg-opacity-90 p-4 shadow-lg backdrop-blur-sm">
        <div className="mb-4">
          <h3 className="mb-2 text-lg font-medium text-white">Mesh Smoothing</h3>
          <p className="text-xs text-gray-300">Adjust parameters to smooth the 3D model</p>
        </div>

        <div className="mb-4">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-300">Relaxation Factor</label>
            <span className="text-sm font-medium text-blue-400">
              {smoothingParams.alpha.toFixed(2)}
            </span>
          </div>
          <div className="relative mt-2">
            <div className="absolute inset-y-0 h-0.5 w-full bg-gray-600"></div>
            <input
              type="range"
              min="0.01"
              max="1"
              step="0.02"
              value={smoothingParams.alpha}
              onChange={e =>
                setSmoothingParams(prev => ({
                  ...prev,
                  alpha: parseFloat(e.target.value),
                }))
              }
              className="relative h-1.5 w-full appearance-none rounded-lg bg-transparent"
              style={{
                background: `linear-gradient(to right, rgb(59, 130, 246) 0%, rgb(59, 130, 246) ${((smoothingParams.alpha - 0.01) / (0.5 - 0.01)) * 100}%, rgb(75, 85, 99) ${((smoothingParams.alpha - 0.01) / (0.5 - 0.01)) * 100}%, rgb(75, 85, 99) 100%)`,
                cursor: 'pointer',
              }}
            />
          </div>
          <div className="mt-1 flex justify-between text-xs text-gray-400">
            <span>0.01</span>
            <span>0.5</span>
          </div>
        </div>

        <div className="mb-4">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-300">Iterations</label>
            <span className="text-sm font-medium text-blue-400">{smoothingParams.beta}</span>
          </div>
          <div className="relative mt-2">
            <div className="absolute inset-y-0 h-0.5 w-full bg-gray-600"></div>
            <input
              type="range"
              min="50"
              max="1000"
              step="50"
              value={smoothingParams.beta}
              onChange={e =>
                setSmoothingParams(prev => ({
                  ...prev,
                  beta: parseInt(e.target.value),
                }))
              }
              className="relative h-1.5 w-full appearance-none rounded-lg bg-transparent"
              style={{
                background: `linear-gradient(to right, rgb(59, 130, 246) 0%, rgb(59, 130, 246) ${((smoothingParams.beta - 50) / (500 - 50)) * 100}%, rgb(75, 85, 99) ${((smoothingParams.beta - 50) / (500 - 50)) * 100}%, rgb(75, 85, 99) 100%)`,
                cursor: 'pointer',
              }}
            />
          </div>
          <div className="mt-1 flex justify-between text-xs text-gray-400">
            <span>50</span>
            <span>500</span>
          </div>
        </div>

        <button
          onClick={() => loadMeshModel(currentMeshType)}
          className="group relative w-full overflow-hidden rounded-md bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-2 text-sm font-medium text-white shadow-lg transition-all duration-200 hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800"
        >
          <span className="relative z-10">Apply Smoothing</span>
          <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover:opacity-100">
            <div className="h-full w-full bg-gradient-to-r from-blue-600 to-blue-700"></div>
          </div>
        </button>
      </div>*/}

      {/* Print Service Dialog */}
      {showPrintDialog && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md rounded-lg bg-gray-800 p-4 shadow-lg">
            <h3 className="mb-4 text-lg font-bold text-white">Send to 3D Printing Service</h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300">Select Service</label>
              <select
                value={selectedPrintService.name}
                onChange={e => {
                  const service = PRINTING_SERVICES.find(s => s.name === e.target.value);
                  if (service) {
                    setSelectedPrintService(service);
                  }
                }}
                className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 p-2 text-white"
              >
                {PRINTING_SERVICES.map(service => (
                  <option
                    key={service.name}
                    value={service.name}
                  >
                    {service.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300">Material</label>
              <select
                value={printOptions.material}
                onChange={e => setPrintOptions({ ...printOptions, material: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 p-2 text-white"
              >
                <option value="plastic">Plastic</option>
                <option value="resin">Resin</option>
                <option value="metal">Metal</option>
                <option value="nylon">Nylon</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300">Print Quality</label>
              <select
                value={printOptions.quality}
                onChange={e => setPrintOptions({ ...printOptions, quality: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 p-2 text-white"
              >
                <option value="draft">Draft (100 microns)</option>
                <option value="standard">Standard (50 microns)</option>
                <option value="high">High (25 microns)</option>
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300">Color</label>
              <select
                value={printOptions.color}
                onChange={e => setPrintOptions({ ...printOptions, color: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 p-2 text-white"
              >
                <option value="white">White</option>
                <option value="black">Black</option>
                <option value="gray">Gray</option>
                <option value="red">Red</option>
                <option value="blue">Blue</option>
                <option value="green">Green</option>
                <option value="yellow">Yellow</option>
              </select>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setShowPrintDialog(false)}
                className="rounded-md bg-gray-600 px-4 py-2 text-white hover:bg-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={sendToPrintService}
                className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-500"
              >
                Send to {selectedPrintService.name}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
